import React, { memo } from 'react'

import { Box, alpha, useTheme } from '@mui/material'

import { float } from '@/utils/animations'

export interface PageBackgroundProps {
  variant?: 'staff' | 'admin' | 'super-admin' | 'landing'
  animate?: boolean
}

const PageBackground = memo<PageBackgroundProps>(({ variant = 'staff', animate = true }) => {
  const theme = useTheme()

  const getGradient = () => {
    const baseGradient =
      theme.palette.mode === 'dark'
        ? 'linear-gradient(180deg, #0F172A 0%, #1E293B 100%)'
        : 'linear-gradient(180deg, #F8FAFC 0%, #E2E8F0 100%)'

    switch (variant) {
      case 'super-admin':
        return theme.palette.mode === 'dark'
          ? 'linear-gradient(135deg, #0F172A 0%, #1E293B 50%, #0F172A 100%)'
          : 'linear-gradient(135deg, #F8FAFC 0%, #EEF2F6 50%, #F8FAFC 100%)'
      case 'admin':
        return baseGradient
      case 'landing':
        return theme.palette.mode === 'dark'
          ? 'linear-gradient(180deg, #0F172A 0%, #1E293B 100%)'
          : 'linear-gradient(180deg, #F8FAFC 0%, #E2E8F0 100%)'
      default:
        return baseGradient
    }
  }

  return (
    <>
      {/* Base Gradient */}
      <Box
        sx={{
          position: 'fixed',
          inset: 0,
          background: getGradient(),
          zIndex: 0,
        }}
      />

      {/* Animated Orbs - Only on desktop for performance */}
      {animate && (
        <Box
          sx={{
            position: 'fixed',
            inset: 0,
            overflow: 'hidden',
            zIndex: 0,
            display: { xs: 'none', md: 'block' },
            '&::before': {
              content: '""',
              position: 'absolute',
              width: '200%',
              height: '200%',
              top: '-50%',
              left: '-50%',
              background:
                theme.palette.mode === 'dark'
                  ? `radial-gradient(circle at 20% 80%, ${alpha('#3B82F6', 0.1)} 0%, transparent 50%), 
                     radial-gradient(circle at 80% 20%, ${alpha('#8B5CF6', 0.1)} 0%, transparent 50%)`
                  : `radial-gradient(circle at 20% 80%, ${alpha('#3B82F6', 0.05)} 0%, transparent 50%), 
                     radial-gradient(circle at 80% 20%, ${alpha('#8B5CF6', 0.05)} 0%, transparent 50%)`,
              animation: `${float} 20s ease-in-out infinite`,
            },
          }}
        />
      )}

      {/* Pattern Overlay - Optional */}
      {variant === 'super-admin' && (
        <Box
          sx={{
            position: 'fixed',
            inset: 0,
            opacity: 0.02,
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='${theme.palette.mode === 'dark' ? '%23ffffff' : '%23000000'}' fill-opacity='0.5'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
            zIndex: 0,
          }}
        />
      )}
    </>
  )
})

PageBackground.displayName = 'PageBackground'

export default PageBackground
